/**
 * 用于利润测算
 * 李波波
 */
import { Calculator } from './calUtil'
import { divideRound, multiplyRound, addRound } from '@/libs/util.number.js'

const ONE_HUNDRED = 100

export function calculate(productInfo) {
  if (!productInfo) return
  const {
    salesPrice = 0,
    commissionRate = 0,
    advertisingRate = 0,
    storageUnitPrice = 0,
    inStorageMonth = 0,
    unitCbm = 0,
    tariffsRate = 0,
    salesReturnRate = 0,
    ftyPrice = 0,
    exchangeRate = 0,
    usdExchangeRate = 0,
    shipUnitPrice = 0,
    truckingFee = 0,
    freightFee = 0,
    salesTaxRate = 0,
    localTaxRate = 0,
    chinaVatRate = 0,
    refundTaxRate = 0,
    clearanceUnitPrice = 0,
    deliveryCost = 0,
    vatRate = 0,
    regionCode = '',
  } = productInfo
  const commissionCost = calcCommissionCost(salesPrice, commissionRate)
  const advertisingCost = calcAdvertisingCost(salesPrice, advertisingRate)
  const storageCost = calcStorageCost(storageUnitPrice, inStorageMonth, unitCbm)
  const shipCost = calcShipCost(shipUnitPrice, unitCbm, exchangeRate)
  let tariffs = 0
  let salesTax = 0
  let localTax = 0
  let vat = calcVat(salesPrice, vatRate)

  if (regionCode !== 'JP') {
    tariffs = calcTariffs(
      clearanceUnitPrice,
      tariffsRate,
      exchangeRate,
      usdExchangeRate,
      regionCode
    )
  } else {
    tariffs = calcTariffs_JP(
      clearanceUnitPrice,
      commissionRate,
      deliveryCost,
      truckingFee,
      freightFee,
      tariffsRate
    )
    salesTax = calcSalesTax(
      clearanceUnitPrice,
      commissionRate,
      deliveryCost,
      truckingFee,
      freightFee,
      tariffsRate,
      tariffs,
      salesTaxRate
    )
    localTax = calcLocalTax(salesTax, localTaxRate)
  }
  const restockingCost = calcRestockingCost(
    deliveryCost,
    shipCost,
    tariffs,
    salesReturnRate,
    ftyPrice,
    exchangeRate
  )
  const refundTax = calcRefundTax(
    ftyPrice,
    chinaVatRate,
    refundTaxRate,
    exchangeRate
  )
  const goodsCost = calcGoodsCost(
    commissionCost,
    deliveryCost,
    advertisingCost,
    storageCost,
    restockingCost,
    shipCost,
    tariffs,
    salesTax,
    localTax,
    vat,
    ftyPrice,
    exchangeRate
  )
  const profit = calcProfit(salesPrice, goodsCost, refundTax)
  const profitRate = calcProfitRate(salesPrice, profit)
  return {
    commissionCost: divideRound(commissionCost, 1, 2),
    advertisingCost: divideRound(advertisingCost, 1, 2),
    storageCost: divideRound(storageCost, 1, 2),
    shipCost: divideRound(shipCost, 1, 2),
    restockingCost: divideRound(restockingCost, 1, 2),
    tariffs: divideRound(tariffs, 1, 2),
    salesTax: divideRound(salesTax, 1, 2),
    localTax: divideRound(localTax, 1, 2),
    vat: divideRound(vat, 1, 2),
    refundTax: divideRound(refundTax, 1, 2),
    goodsCost: divideRound(goodsCost, 1, 2),
    profit: divideRound(profit, 1, 2),
    profitRate: divideRound(profitRate, 1, 2),
  }
}

/**
 * 佣金花费
 * @param {Number} salesPrice 售价
 * @param {Number} commissionRate 佣金比例
 * @returns
 */
export function calcCommissionCost(salesPrice, commissionRate) {
  // 售价 * 佣金比例
  return Calculator.init(salesPrice)
    .multiply(commissionRate, 8)
    .divide(ONE_HUNDRED, 8) // 比例为15 => 15/100 => 0.15
    .getResult()
}

/**
 * 广告花费
 * @param {Number} salesPrice 售价
 * @param {Number} advertisingRate 广告比例
 * @returns
 */
export function calcAdvertisingCost(salesPrice, advertisingRate) {
  // 售价 * 广告比例
  return Calculator.init(salesPrice)
    .multiply(advertisingRate, 8)
    .divide(ONE_HUNDRED, 8)
    .getResult()
}

/**
 * 仓储花费
 * @param {*} storageUnitPrice 仓储单价
 * @param {*} inStorageMonth 在库月
 * @param {*} unitCbm 单品体积
 * @returns
 */
export function calcStorageCost(storageUnitPrice, inStorageMonth, unitCbm) {
  // 仓储单价 * 在库月 * 单品体积
  return Calculator.init(storageUnitPrice)
    .multiply(inStorageMonth, 8)
    .multiply(unitCbm, 8)
    .getResult()
}

/**
 * 头程花费
 * @param {*} shipUnitPrice 头程拼柜单价
 * @param {*} unitCbm 单品体积
 * @param {*} exchangeRate 汇率
 * @returns
 */
export function calcShipCost(shipUnitPrice, unitCbm, exchangeRate) {
  // （头程拼柜单价 * 单品体积）/ 汇率
  return Calculator.init(shipUnitPrice)
    .multiply(unitCbm, 8)
    .divide(exchangeRate, 8)
    .getResult()
}

/**
 * 关税 - 不包含日本
 * @param {*} clearanceUnitPrice 清关单价
 * @param {*} tariffsRate 关税率
 * @returns
 */
export function calcTariffs(
  clearanceUnitPrice,
  tariffsRate,
  exchangeRate,
  usdExchangeRate,
  regionCode
) {
  if (regionCode !== 'US') {
    // (清关单价 * 关税率) * 美元汇率 / 汇率
    return Calculator.init(clearanceUnitPrice)
      .multiply(tariffsRate, 8)
      .divide(ONE_HUNDRED, 8)
      .multiply(usdExchangeRate, 8)
      .divide(exchangeRate, 8)
      .getResult()
  } else {
    // 清关单价 * 关税率
    return Calculator.init(clearanceUnitPrice)
      .multiply(tariffsRate, 8)
      .divide(ONE_HUNDRED, 8)
      .getResult()
  }
}

/**
 * 关税 - 日本
 * @param {*} clearanceUnitPrice 清关单价
 * @param {*} commissionRate 佣金率
 * @param {*} deliveryCost 配送费
 * @param {*} truckingFee 货代卡车费
 * @param {*} freightFee 货代头程运费
 * @param {*} tariffsRate 关税率
 */
export function calcTariffs_JP(
  clearanceUnitPrice,
  commissionRate,
  deliveryCost,
  truckingFee,
  freightFee,
  tariffsRate
) {
  //  (（清关单价 - 清关单价*佣金率 -配送费 - 货代卡车费 - 货代头程运费 ）/ 1.1 / (1+关税率) ) * 关税率
  // 清关单价*佣金率
  const commissionRatePre = divideRound(commissionRate, ONE_HUNDRED, 8)
  const clearanceMulCommission = multiplyRound(
    clearanceUnitPrice,
    commissionRatePre,
    8
  )
  //  (1+关税率)
  const tariffsRatePre = divideRound(tariffsRate, ONE_HUNDRED, 8)
  const tarffsRateAddOne = addRound(1, tariffsRatePre, 8)

  return Calculator.init(clearanceUnitPrice)
    .subtract(clearanceMulCommission, 8)
    .subtract(deliveryCost, 8)
    .subtract(truckingFee, 8)
    .subtract(freightFee, 8)
    .divide(1.1, 8)
    .divide(tarffsRateAddOne, 8)
    .multiply(tariffsRatePre, 8)
    .getResult()
}

/**
 * 消费税（日本）
 * @param {*} clearanceUnitPrice 清关单价
 * @param {*} commissionRate 佣金率
 * @param {*} deliveryCost 配送费
 * @param {*} truckingFee 货代卡车费
 * @param {*} freightFee 货代头程运费
 * @param {*} tariffsRate 关税率
 * @param {*} tariffs 关税
 * @param {*} salesTaxRate 消费税率
 * @returns
 */
export function calcSalesTax(
  clearanceUnitPrice,
  commissionRate,
  deliveryCost,
  truckingFee,
  freightFee,
  tariffsRate,
  tariffs,
  salesTaxRate
) {
  /*
  （（清关单价- 清关单价* 佣金率 - 配送费 - 货代卡车费 - 货代头程运费 ） / 1.1 / (1+关税率) + 关税 ）* 消费税率
   */

  // 清关单价*佣金率
  const commissionRatePre = divideRound(commissionRate, ONE_HUNDRED, 8)
  const clearanceMulCommission = multiplyRound(
    clearanceUnitPrice,
    commissionRatePre,
    8
  )
  //  (1+关税率)
  const tariffsRatePre = divideRound(tariffsRate, ONE_HUNDRED, 8)
  const tarffsRateAddOne = addRound(1, tariffsRatePre, 8)
  return Calculator.init(clearanceUnitPrice)
    .subtract(clearanceMulCommission, 8)
    .subtract(deliveryCost, 8)
    .subtract(truckingFee, 8)
    .subtract(freightFee, 8)
    .divide(1.1, 8)
    .divide(tarffsRateAddOne, 8)
    .add(tariffs, 8)
    .multiply(salesTaxRate, 8)
    .divide(ONE_HUNDRED, 8)
    .getResult()
}

/**
 * 地方税（日本）
 * @param {*} salesTax 消费税
 * @param {*} localTaxRate 地方税率
 */
export function calcLocalTax(salesTax, localTaxRate) {
  // 消费税（日本）* 地方税率
  return Calculator.init(salesTax)
    .multiply(localTaxRate, 8)
    .divide(ONE_HUNDRED, 8)
    .getResult()
}
/**
 * 退货花费
 * @param {*} deliveryCost 配送费
 * @param {*} shipCost 头程花费
 * @param {*} tariffs 关税
 * @param {*} salesReturnRate 退货率
 * @param {*} ftyPrice 出厂价
 * @param {*} exchangeRate 汇率
 */
export function calcRestockingCost(
  deliveryCost,
  shipCost,
  tariffs,
  salesReturnRate,
  ftyPrice,
  exchangeRate
) {
  // （配送费 + 头程花费 + 关税）* 退货率 + （出厂价 * 退货率）/ 汇率

  // （出厂价 * 退货率）/ 汇率
  const ftyReturnPrice = Calculator.init(ftyPrice)
    .multiply(salesReturnRate, 8)
    .divide(ONE_HUNDRED, 8)
    .divide(exchangeRate, 8)
    .getResult()

  // （配送费 + 头程花费 + 关税）* 退货率 + （出厂价 * 退货率）/ 汇率
  return Calculator.init(deliveryCost)
    .add(shipCost, 8)
    .add(tariffs, 8)
    .multiply(salesReturnRate, 8)
    .divide(ONE_HUNDRED, 8)
    .add(ftyReturnPrice, 8)
    .getResult()
}
/**
 * 国外增值税
 * @param {*} salesPrice 售价
 * @param {*} vatRate VAT增值税率
 * @returns
 */
export function calcVat(salesPrice, vatRate) {
  // 售价 - 售价/(1 + VAT增值税率)

  // (1 + VAT增值税率)
  const vatRateAddOne = Calculator.init(vatRate)
    .divide(ONE_HUNDRED, 8)
    .add(1, 8)
    .getResult()
  // 售价/(1 + VAT增值税率)
  const salesDivVra = divideRound(salesPrice, vatRateAddOne, 8)
  return Calculator.init(salesPrice)
    .subtract(salesDivVra, 8)
    .getResult()
}

/**
 * 退税
 * @param {*} ftyPrice 出厂价
 * @param {*} chinaVatRate 国内增值税率
 * @param {*} refundTaxRate 退税率
 * @param {*} exchangeRate 汇率
 * @returns
 */
export function calcRefundTax(
  ftyPrice,
  chinaVatRate,
  refundTaxRate,
  exchangeRate
) {
  // （出厂价 / （1+国内增值税率）* 退税率）/ 汇率

  // （1+国内增值税率）
  const chinaVatRatePre = divideRound(chinaVatRate, ONE_HUNDRED, 8)
  const chinaVatRateAddOne = addRound(chinaVatRatePre, 1, 8)
  return Calculator.init(ftyPrice)
    .divide(chinaVatRateAddOne, 8)
    .multiply(refundTaxRate, 8)
    .divide(ONE_HUNDRED, 8)
    .divide(exchangeRate, 8)
    .getResult()
}

/**
 * 产品成本
 * @param {*} commissionCost 佣金
 * @param {*} deliveryCost 配送费
 * @param {*} advertisingCost 广告费
 * @param {*} storageCost 仓储费
 * @param {*} restockingCost 退货费
 * @param {*} shipCost 头程花费
 * @param {*} tariffs 关税
 * @param {*} salesTax 消费税
 * @param {*} localTax 地方税
 * @param {*} vat VAT
 * @param {*} ftyPrice 出厂价
 * @param {*} exchangeRate 汇率
 * @returns
 */
export function calcGoodsCost(
  commissionCost,
  deliveryCost,
  advertisingCost,
  storageCost,
  restockingCost,
  shipCost,
  tariffs,
  salesTax,
  localTax,
  vat,
  ftyPrice,
  exchangeRate
) {
  // 佣金 + 配送费 + 广告费 + 仓储费 + 退货费 + 头程花费 + 关税 + 消费税 + 地方税 + VAT +（出厂价 / 汇率）

  // （出厂价 / 汇率）
  const ftyPriceExchange = divideRound(ftyPrice, exchangeRate, 8)
  return Calculator.init(commissionCost)
    .add(deliveryCost, 8)
    .add(advertisingCost, 8)
    .add(storageCost, 8)
    .add(restockingCost, 8)
    .add(shipCost, 8)
    .add(tariffs, 8)
    .add(salesTax, 8)
    .add(localTax, 8)
    .add(vat, 8)
    .add(ftyPriceExchange, 8)
    .getResult()
}

/**
 * 毛利
 * @param {*} salesPrice 售价
 * @param {*} goodsCost 产品成本
 * @param {*} refundTax 退税
 * @returns
 */
export function calcProfit(salesPrice, goodsCost, refundTax) {
  // 售价 - 产品成本 + 退税
  return Calculator.init(salesPrice)
    .subtract(goodsCost, 8)
    .add(refundTax, 8)
    .getResult()
}

/**
 * 毛利率
 * @param {*} salesPrice 售价
 * @param {*} profit 毛利
 * @returns
 */
export function calcProfitRate(salesPrice, profit) {
  // 毛利 / 售价
  return Calculator.init(profit)
    .divide(salesPrice, 8)
    .multiply(ONE_HUNDRED, 8)
    .getResult()
}

export default {
  methods: {
    calculate,
    calcCommissionCost,
    calcAdvertisingCost,
    calcStorageCost,
    calcRestockingCost,
    calcShipCost,
    calcTariffs,
    calcTariffs_JP,
    calcSalesTax,
    calcLocalTax,
    calcVat,
    calcRefundTax,
    calcGoodsCost,
    calcProfit,
    calcProfitRate,
  },
}
